"use client";

import { useTranslations } from "next-intl";
import { useRouter, useSearchParams } from "next/navigation";
import { useState, type SubmitEvent } from "react";
import { Button } from "@/components/ui/Button";
import { signIn } from "@/lib/auth-client";
import { useProjectConfigs } from "@/features/project/ProjectContext";
import { logoUrl } from "@/features/media/mediaUrl";
import { useMediaContext } from "@/features/media/useMediaContext";

/** Only follow a same-origin, absolute path — never an attacker-supplied URL. */
function safeNext(next: string | null): string {
  return next && next.startsWith("/") && !next.startsWith("//") ? next : "/";
}

/** An `@` means the identifier is an email; anything else is treated as a username. */
function looksLikeEmail(identifier: string): boolean {
  return identifier.includes("@");
}

function isMicrosoftAuth(authType: string | undefined): boolean {
  return authType != null && authType.toLowerCase() === "sso";
}

/**
 * Sign-in for a protected map.
 *
 * A tenant is either **credential** (email/username + password) or **Microsoft**
 * (`authType`), never both — the customer's Entra directory is the whole access
 * list, so showing a password box there would be dead UI. The logo is shared; the
 * controls below it are one or the other.
 */
export function LoginForm() {
  const t = useTranslations();
  const configs = useProjectConfigs();
  const { domain } = useMediaContext();

  const logo = logoUrl(configs?.appLogos?.[0]?.image?.fileName, domain);

  return (
    <div className="flex flex-col justify-center gap-4">
      {logo && (
        <a
          href={configs?.appLogos?.[0]?.url}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center"
        >
          {
            // eslint-disable-next-line @next/next/no-img-element
            <img className="h-[36px]" src={logo} alt={t("logo")} />
          }
        </a>
      )}

      {isMicrosoftAuth(configs?.authType) ? (
        <MicrosoftSignIn domain={domain} />
      ) : (
        <PasswordSignIn />
      )}
    </div>
  );
}

/**
 * Microsoft/Entra SSO. `signIn.sso` hands the browser off to the tenant's Entra
 * app (keyed by `providerId`, which the CLI registers as the project domain) and
 * returns only on failure — on success the browser has already left for the IdP,
 * so there is nothing to do after the await.
 */
function MicrosoftSignIn({ domain }: { domain: string | undefined }) {
  const t = useTranslations();
  const next = safeNext(useSearchParams().get("next"));
  const [pending, setPending] = useState(false);
  const [failed, setFailed] = useState(false);

  async function onClick() {
    if (!domain) return;
    setPending(true);
    setFailed(false);

    const { error } = await signIn.sso({
      providerId: domain,
      // Absolute so the post-IdP redirect lands back on this tenant's origin, not
      // the shared auth host the callback runs on.
      callbackURL: `${window.location.origin}${next}`,
    });
    if (error) {
      setFailed(true);
      setPending(false);
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <p className="text-base text-center">{t("login.subtitle")}</p>

      {failed && (
        <p role="alert" className="text-sm text-red-600 dark:text-red-400">
          {t("login.error")}
        </p>
      )}

      <Button type="button" onClick={onClick} disabled={pending}>
        {pending ? t("login.submitting") : t("login.microsoft")}
      </Button>
    </div>
  );
}

/**
 * Email/username + password. On success the session cookie is set by the backend
 * and we do a full `router.refresh()` before navigating: the target page is
 * server-gated, so it must be re-rendered with the new cookie for the map to
 * appear. Sign-up is closed (accounts are provisioned), so there is no register
 * link.
 */
function PasswordSignIn() {
  const t = useTranslations();
  const router = useRouter();
  const next = safeNext(useSearchParams().get("next"));

  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [pending, setPending] = useState(false);
  const [failed, setFailed] = useState(false);

  async function onSubmit(event: SubmitEvent) {
    event.preventDefault();
    setPending(true);
    setFailed(false);

    const { error } = looksLikeEmail(identifier)
      ? await signIn.email({ email: identifier, password })
      : await signIn.username({ username: identifier, password });
    if (error) {
      setFailed(true);
      setPending(false);
      return;
    }

    // Re-render the server-gated destination with the freshly set cookie.
    router.replace(next);
    router.refresh();
  }

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
      <label className="flex flex-col gap-1 text-base">
        <span>{t("login.username")}</span>
        <input
          type="text"
          name="username"
          autoComplete="username"
          required
          value={identifier}
          placeholder={t("login.username")}
          onChange={(e) => setIdentifier(e.target.value)}
          className="rounded-lg border border-gray-200 bg-white px-3 py-3 text-base text-black outline-none focus:border-[var(--primary)] focus:ring-2 focus:ring-gray-100 dark:border-gray-700 dark:bg-black dark:text-off-white dark:focus:ring-gray-800"
        />
      </label>

      <label className="flex flex-col gap-1 text-base">
        <span>{t("login.password")}</span>
        <input
          type="password"
          name="password"
          autoComplete="current-password"
          required
          value={password}
          placeholder={t("login.password")}
          onChange={(e) => setPassword(e.target.value)}
          className="rounded-lg border border-gray-200 bg-white px-3 py-3 text-base text-black outline-none focus:border-[var(--primary)] focus:ring-2 focus:ring-gray-100 dark:border-gray-700 dark:bg-black dark:text-off-white dark:focus:ring-gray-800"
        />
      </label>

      {failed && (
        <p role="alert" className="text-sm text-red-600 dark:text-red-400">
          {t("login.error")}
        </p>
      )}

      <div className="flex justify-end mt-2">
        <Button type="submit" disabled={pending}>
          {pending ? t("login.submitting") : t("login.button")}
        </Button>
      </div>
    </form>
  );
}
